import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Setup axios interceptor for auto token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// Interceptor: on 401, try to refresh the token
axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    
    // Don't retry refresh, login, register, or already retried requests
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/register')
    ) {
      return Promise.reject(error);
    }
    
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => axios(originalRequest));
    }
    
    originalRequest._retry = true;
    isRefreshing = true;
    
    try {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      processQueue(null);
      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      // Refresh failed — clear user state
      localStorage.removeItem('hub_user');
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Try to restore user from localStorage on initial load
    const savedUser = localStorage.getItem('hub_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('hub_user', JSON.stringify(user));
    }
  }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      localStorage.setItem('hub_user', JSON.stringify(response.data));
      setError(null);
    } catch (err) {
      // Don't clear user immediately - check localStorage
      const savedUser = localStorage.getItem('hub_user');
      if (!savedUser) {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, extraData = {}) => {
    try {
      const loginPayload = { email, password, ...extraData };
      const response = await axios.post(`${API}/auth/login`, 
        loginPayload,
        { withCredentials: true }
      );
      
      // Check if MFA is required (backend returns mfa_required instead of user)
      if (response.data?.mfa_required) {
        return response.data;
      }
      
      setUser(response.data.user);
      setError(null);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(typeof message === 'string' ? message : message.message || 'Login failed');
      throw err;
    }
  };

  const register = async (email, name, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`,
        { email, name, password },
        { withCredentials: true }
      );
      setUser(response.data.user);
      setError(null);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed';
      setError(message);
      throw new Error(message);
    }
  };

  const processGoogleSession = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      setUser(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Google login failed';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      localStorage.removeItem('hub_user');
      // Session token is cleared via HttpOnly cookie deletion by backend
    }
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    // This would need to check against the user's role permissions
    // For now, we'll check based on role level
    const adminRoles = ['master_admin', 'admin'];
    if (adminRoles.includes(user.role_id)) return true;
    return false;
  };

  const isAdmin = () => {
    if (!user) return false;
    return ['master_admin', 'admin'].includes(user.role_id);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      login,
      register,
      logout,
      processGoogleSession,
      refreshUser,
      hasPermission,
      isAdmin,
      setUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
